/** @format */

import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
	ShieldCheck,
	AlertTriangle,
	ListChecks,
	MessageSquare,
	Phone,
	Mail,
	CalendarDays,
	Check,
} from "lucide-react"; // Refined icons
import ContactBlock from "../../components/contact/ContactBlock"; // Assuming path
import ReactMarkdown from "react-markdown";

// --- Warranty Data Structure (Revised) ---
const warrantyInfo = {
	coverage: {
		title: "What's Covered: Our 5-Year Promise",
		icon: ShieldCheck,
		points: [
			"We take pride in our locally crafted shades, made in Toronto with top-grade materials. We expect them to operate flawlessly for years under normal use.",
			"This warranty covers **manufacturing defects** in materials and workmanship that appear within **five (5) years** from the original date of purchase.",
			"If a defect arises during this period, contact us. We will, at our discretion, repair or replace the defective product.",
			"This promise extends only to the **original purchaser** and applies only while the product remains in the window it was initially installed in.",
		],
	},
	exceptions: {
		title: "What's Not Covered",
		icon: AlertTriangle,
		points: [
			"**Normal Wear & Tear:** Gradual fading, fraying, or loss of colour intensity over time.",
			"**Accidents & Misuse:** Damage from accidents, improper handling, abuse, alterations, or use outside normal conditions.",
			"**Environmental Factors:** Damage from excessive humidity, heat, exposure to salty air, or failure to follow cleaning instructions.",
			"**Installation/Measurement Issues:** Problems arising from incorrect measurement, improper installation (not following our guides), or removal/reinstallation.",
			"**Batteries & Normal Motor Maintenance:** Recharging motor batteries or routine motor upkeep.",
			"**Associated Costs:** Shipping costs for warranty service, labor costs for removal, measurement, or reinstallation.",
		],
	},
	claimProcess: {
		title: "How to Make a Warranty Claim",
		icon: ListChecks,
		steps: [
			"**Contact Us Promptly:** Reach out to our Customer Service team via email or phone as soon as you notice an issue.",
			"**Provide Details:** Include your original order number (or proof of purchase), a description of the problem, and photos/videos clearly showing the defect.",
			"**Assessment:** Our team will review your claim and may ask for additional information to determine if the issue is covered under warranty.",
			"**Resolution:** If covered, we'll guide you through the next steps for repair or replacement.",
		],
		contactNote: "Find our contact details below or visit our Contact page.",
	},
};

// --- Main Warranty Screen Component ---
const WarrantyScreen = () => {
	return (
		<div className="overflow-x-hidden bg-gradient-to-b from-white to-gray-100">
			{" "}
			{/* Subtle gradient */}
			<Helmet>
				<title>
					5-Year Window Shade Warranty | Coverage & Claims | Off Cut Shades
				</title>{" "}
				{/* SEO Optimized Title */}
				<meta
					name="description"
					content="Learn about Off Cut Shades' comprehensive 5-year warranty covering manufacturing defects on custom window shades. Find out what's covered, exceptions, and how to make a claim."
				/>{" "}
				{/* SEO Optimized Desc */}
				<link rel="canonical" href="https://offcutshades.com/help/warranty" />
			</Helmet>
			<main className="pt-24 pb-16 md:pt-32 md:pb-24">
				{" "}
				{/* Added top padding */}
				{/* --- Hero Section --- */}
				<section className="container mx-auto px-4 text-center mb-12 md:mb-16">
					<motion.div
						initial={{ scale: 0.8, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						transition={{ type: "spring", stiffness: 150, delay: 0.1 }}
						className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4"
					>
						<ShieldCheck size={36} strokeWidth={1.5} /> {/* Main Icon */}
					</motion.div>
					<motion.h1
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.2 }}
						className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
					>
						Our 5-Year Peace of Mind Warranty
					</motion.h1>
					<motion.p
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.3 }}
						className="text-lg text-gray-600 max-w-2xl mx-auto"
					>
						We stand behind the quality of our Canadian-made shades. Learn about
						your warranty coverage below.
					</motion.p>
				</section>
				{/* --- Warranty Details Sections --- */}
				<section className="container mx-auto px-4 max-w-4xl mx-auto">
					<div className="space-y-8 md:space-y-10">
						{/* What's Covered */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.5 }}
							className="bg-white rounded-xl shadow-md border border-gray-200 p-6 md:p-8"
						>
							<div className="flex flex-col sm:flex-row items-start gap-4 mb-4">
								<div className="w-10 h-10 rounded-lg bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0">
									<warrantyInfo.coverage.icon size={24} strokeWidth={2} />
								</div>
								<h2 className="text-xl md:text-2xl font-semibold text-gray-800 mt-1">
									{warrantyInfo.coverage.title}
								</h2>
							</div>
							{/* Use list for better structure */}
							<ul className="space-y-3 text-gray-700 text-sm sm:text-base pl-0 sm:pl-14">
								{warrantyInfo.coverage.points.map((point, pIndex) => (
									<li key={`cov-${pIndex}`} className="flex items-start gap-2">
										<Check
											size={16}
											className="text-green-500 mt-1 flex-shrink-0"
											strokeWidth={2.5}
										/>
										<ReactMarkdown>{point}</ReactMarkdown>
									</li>
								))}
							</ul>
						</motion.div>

						{/* What's Not Covered */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.5, delay: 0.1 }}
							className="bg-white rounded-xl shadow-md border border-gray-200 p-6 md:p-8"
						>
							<div className="flex flex-col sm:flex-row items-start gap-4 mb-4">
								<div className="w-10 h-10 rounded-lg bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0">
									<warrantyInfo.exceptions.icon size={24} strokeWidth={2} />
								</div>
								<h2 className="text-xl md:text-2xl font-semibold text-gray-800 mt-1">
									{warrantyInfo.exceptions.title}
								</h2>
							</div>
							<ul className="space-y-3 text-gray-700 text-sm sm:text-base pl-0 sm:pl-14">
								{warrantyInfo.exceptions.points.map((point, pIndex) => (
									<li key={`exc-${pIndex}`} className="flex items-start gap-2">
										{/* Can use different icon or simple bullet */}
										<AlertTriangle
											size={16}
											className="text-red-400 mt-1 flex-shrink-0"
											strokeWidth={2}
										/>
										<ReactMarkdown>{point}</ReactMarkdown>
									</li>
								))}
							</ul>
						</motion.div>

						{/* How to Make a Claim */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.5, delay: 0.2 }}
							className="bg-white rounded-xl shadow-md border border-gray-200 p-6 md:p-8"
						>
							<div className="flex flex-col sm:flex-row items-start gap-4 mb-4">
								<div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
									<warrantyInfo.claimProcess.icon size={24} strokeWidth={2} />
								</div>
								<h2 className="text-xl md:text-2xl font-semibold text-gray-800 mt-1">
									{warrantyInfo.claimProcess.title}
								</h2>
							</div>
							<ol className="space-y-3 text-gray-700 text-sm sm:text-base list-decimal list-outside pl-5 sm:pl-14">
								{warrantyInfo.claimProcess.steps.map((step, pIndex) => (
									<li key={`claim-${pIndex}`}>
										<ReactMarkdown>{step}</ReactMarkdown>
									</li>
								))}
							</ol>
							<p className="text-sm text-gray-600 mt-4 pl-0 sm:pl-14">
								{warrantyInfo.claimProcess.contactNote}
							</p>
							{/* Add Direct Contact Links Here */}
							<div className="mt-4 pl-0 sm:pl-14 flex flex-col sm:flex-row gap-4">
								<Link
									to="/contact"
									className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border-2 border-primary text-primary rounded-lg font-semibold text-sm hover:bg-primary/10 transition-colors"
								>
									<MessageSquare size={16} /> Contact Customer Service
								</Link>
								{/* Or provide email/phone directly */}
								{/* <a href="mailto:support@..." className="...">...</a> */}
								{/* <a href="tel:+1..." className="...">...</a> */}
							</div>
						</motion.div>
					</div>
				</section>
			</main>
			{/* --- Contact Block --- */}
			<ContactBlock
				title="Questions About Your Warranty?"
				description="Our team is happy to clarify any details about your coverage or help you start a claim."
			/>
		</div>
	);
};

export default WarrantyScreen;
